import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsFetching } from "@tanstack/react-query";

const MIN_VISIBLE_MS = 500;
const MAX_VISIBLE_MS = 4000;

/**
 * Dil değişiminde ekranda "içerik güncelleniyor" geçişi gösterir.
 * Overlay, dile bağlı react-query sorguları yeniden çekilene kadar
 * (en az 500ms, en fazla 4sn) açık kalır.
 */
const LanguageTransitionOverlay = () => {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState(false);
  const startedAt = useRef(0);
  const fetching = useIsFetching();

  useEffect(() => {
    const onChange = () => {
      startedAt.current = Date.now();
      setActive(true);
    };
    i18n.on("languageChanged", onChange);
    return () => {
      i18n.off("languageChanged", onChange);
    };
  }, [i18n]);

  useEffect(() => {
    if (!active) return;

    const elapsed = Date.now() - startedAt.current;
    const hardStop = setTimeout(() => setActive(false), Math.max(0, MAX_VISIBLE_MS - elapsed));

    let softStop: ReturnType<typeof setTimeout> | undefined;
    if (fetching === 0) {
      softStop = setTimeout(() => setActive(false), Math.max(MIN_VISIBLE_MS - elapsed, 250));
    }

    return () => {
      clearTimeout(hardStop);
      if (softStop) clearTimeout(softStop);
    };
  }, [active, fetching]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key="lang-transition"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          role="status"
          aria-live="polite"
          className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.94, y: 8 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className="glass-card rounded-2xl px-8 py-7 flex flex-col items-center gap-3 text-center"
          >
            <div className="relative">
              <Globe className="text-primary" size={28} />
              <Loader2 className="absolute -right-3 -bottom-2 animate-spin text-primary" size={16} />
            </div>
            <p className="font-display text-base text-foreground">
              {t("common.switchingLanguage")}
            </p>
            <p className="text-xs text-muted-foreground">{t("common.updatingContent")}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LanguageTransitionOverlay;
